import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';
import { UIErrorCode } from './ui-error-code.enum'; // Assuming you have your enum in a file named `ui-error-code.enum.ts`
import { CustomSnackbarComponent } from '../core/custom-snack-bar/custom-snackbar.component';
import { SafeHtml } from '@angular/platform-browser';
import { environment } from '../../environments/environment';

@Injectable()
export class ErrorHandlingInterceptor implements HttpInterceptor {
  constructor(private snackBar: MatSnackBar) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 403 || error.status === 401) {
          this.handleUIErrorCode(UIErrorCode.UNAUTHORIZED);
        } else if (error?.error?.ErrorCode) {
          this.handleUIErrorCode(error.error.ErrorCode);
        }

        return throwError(() => new Error('Something went wrong; please try again later.'));
      }),
    );
  }

  private handleUIErrorCode(errorCode: UIErrorCode): void {
    let errorMessage = this.getMessageForErrorCode(errorCode);

    if (errorMessage)
      this.snackBar.openFromComponent(CustomSnackbarComponent, {
        data: { message: errorMessage },
        panelClass: ['custom-snackbar'],
        duration: 10000,
      });
  }

  private getMessageForErrorCode(errorCode: UIErrorCode): string | SafeHtml | undefined {
    const contactUsLink = `<a href='${environment.externalLinks.contactUs}' target='_blank'>contact us</a>`;
    const contactUsSection = ` If the issue persists, please ${contactUsLink}.`;

    switch (errorCode) {
      // Authentication Errors
      case UIErrorCode.UNAUTHORIZED:
        return (
          'Your session has expired. Please close the AFAS app and launch it again to continue.' +
          contactUsSection
        );

      case UIErrorCode.AUTH_INVALID_INPUT:
        return (
          'Something went wrong with your user session. Please close the AFAS app and launch it again to continue.' +
          contactUsSection
        );

      // Call Sequence Errors
      case UIErrorCode.CALL_SEQUENCE_NOT_FOUND:
        return (
          'We could not load the history. Please click the refresh button to try again.' +
          contactUsSection
        );

      // Callback location errors
      case UIErrorCode.CREATE_UPDATE_PROVIDER_CONTACT:
        return 'We could not save the callback location. Please try again.' + contactUsSection;

      case UIErrorCode.CREATE_UPDATE_PROVIDER_CONTACT_INVALID_INPUT:
        return 'We could not save the callback location. Please ensure that the name is valid and the phone number has more than 2 characters.';

      case UIErrorCode.DELETE_PROVIDER_CONTACT:
        return 'We could not delete the callback location. Please try again.' + contactUsSection;

      case UIErrorCode.DELETE_PROVIDER_CONTACT_INVALID_INPUT:
        return 'We could not delete the callback location. Please try again.' + contactUsSection;

      case UIErrorCode.LIST_PROVIDERS:
        return (
          'We could not load the callback location list. Please close the AFAS app and launch it again.' +
          contactUsSection
        );

      case UIErrorCode.LIST_PROVIDERS_INVALID_INPUT:
        return (
          'We could not load the callback location list. Please close the AFAS app and launch it again.' +
          contactUsSection
        );

      // Contact errors
      case UIErrorCode.CREATE_UPDATE_PATIENT_CONTACT:
        return 'We could not save the contact. Please try again.' + contactUsSection;

      case UIErrorCode.CREATE_UPDATE_PATIENT_CONTACT_INVALID:
        return 'We could not save the contact. Please ensure that the name is valid and the phone number has more than 2 characters.';

      case UIErrorCode.DELETE_PATIENT_CONTACT:
        return 'We could not delete the contact. Please try again.' + contactUsSection;

      case UIErrorCode.DELETE_PATIENT_CONTACT_INVALID_INPUT:
        return 'We could not delete the contact. Please try again.' + contactUsSection;

      case UIErrorCode.LIST_PATIENT_CONTACT:
        return (
          'We could not load the contact list. Please close the AFAS app and launch it again.' +
          contactUsSection
        );

      case UIErrorCode.LIST_PATIENT_CONTACT_INVALID_INPUT:
        return (
          'We could not load the contact list. Please close the AFAS app and launch it again.' +
          contactUsSection
        );

      // Calls/Messages Errors
      case UIErrorCode.LIST_CALLS_MESSAGES:
        return (
          'We could not load the history. Please click the refresh button to try again.' +
          contactUsSection
        );

      // Alert Contact Errors
      case UIErrorCode.NOTIFY_CONTACT:
        return 'We could not alert the contact. Please try again.' + contactUsSection;

      case UIErrorCode.NOTIFY_CONTACT_NO_REQUEST:
        return (
          'We could not alert the contact. Please close the AFAS app and launch it again.' +
          contactUsSection
        );

      case UIErrorCode.NOTIFY_CONTACT_NO_PATIENT:
        return (
          'We could not alert the contact. Please close the AFAS app and launch it again.' +
          contactUsSection
        );

      case UIErrorCode.NOTIFY_CONTACT_NO_PROVIDER:
        return (
          'We could not alert the contact. Please close the AFAS app and launch it again.' +
          contactUsSection
        );
      case UIErrorCode.NOTIFY_CONTACT_US_PHONE:
        return (
          'We could not alert the contact. Please close the AFAS app and launch it again.' +
          contactUsSection
        );

      // Logging Errors
      case UIErrorCode.LOG_EVENT:
        return undefined;

      case UIErrorCode.LOG_EVENT_INVALID:
        return undefined;

      case UIErrorCode.LOG_ERROR:
        return undefined;

      case UIErrorCode.LOG_ERROR_INVALID:
        return undefined;

      case UIErrorCode.ORG_USER_MANAGEMENT:
        return (
          'We could not complete that user management action. Please try again.' + contactUsSection
        );

      // Default case for unexpected errors
      default:
        return (
          'Something went wrong. Please close the AFAS app and launch it again.' + contactUsSection
        );
    }
  }
}
